<template>
  <div>
    <v-container fluid>
      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <h2 style="font-weight: 600; color: #171717;">{{ $t('toolManagement') }}</h2>
              <p style="margin: 4px 0 0; color: #64748b; font-size: 13px;">{{ $t('_manageReusableApiTools') }}</p>
            </v-col>
            <v-col cols="6" class="d-flex flex-wrap justify-end gap-2">
              <v-row align="center" justify="end" style="gap: 20px;">
                <v-text-field
                  v-model="search"
                  :placeholder="$t('searchTools')"
                  hide-details
                  variant="plain"
                  clearable
                  class="search-input"
                  density="comfortable"
                  @keyup.enter="reload"
                >
                  <template #prepend-inner>
                    <v-icon class="search-icon" size="18" color="#9ca3af">mdi-magnify</v-icon>
                  </template>
                </v-text-field>

                <v-btn variant="tonal" color="primary" prepend-icon="mdi-refresh" :loading="loading" @click="reload" class="refresh-btn">
                  {{ $t('refresh') }}
                </v-btn>

                <button type="button" class="tool-add" @click="openCreateDialog">
                  <v-icon size="20" color="white">mdi-plus</v-icon>
                  <span style="color: #ffffff; font-size: 14px;">{{ $t('createTool') }}</span>
                </button>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>

        <v-alert v-if="storageMessage" type="warning" variant="tonal" density="comfortable" class="mx-4 mb-3">
          {{ storageMessage }}
        </v-alert>

        <v-data-table-server
          v-model:options="options"
          :headers="headers"
          :items="tools"
          :items-length="total"
          :loading="loading"
          item-value="id"
          class="pa-4"
        >
          <template v-slot:bottom>
            <div class="d-flex justify-space-between align-center px-4 py-2" style="width: 100%">
              <div>{{ $t('totalResults') }} {{ total }}</div>
              <v-data-table-footer
                :items="[
                  { text: '10', value: 10 },
                  { text: '25', value: 25 },
                  { text: '50', value: 50 },
                ]"
                :page="options.page"
                :length="Math.ceil(total / (options.itemsPerPage || 10))"
              />
            </div>
          </template>

          <template v-slot:no-data>
            <div class="no-data-container">
              <div class="no-data-content">
                <v-icon size="64" color="grey-lighten-2">mdi-tools</v-icon>
                <div class="no-data-text">
                  <h3 class="no-data-title">{{ $t('noToolsFound') }}</h3>
                  <p class="no-data-subtitle">{{ $t('_createFirstTool') }}</p>
                </div>
              </div>
            </div>
          </template>

          <template v-slot:item="{ item }">
            <tr>
              <td style="text-align: left;">{{ item.name }}</td>
              <td style="text-align: left;">{{ item.description }}</td>
              <td style="text-align: left;">
                <span :class="['method-badge', 'method-badge--' + item.httpMethod]">{{ item.httpMethod }}</span>
              </td>
              <td style="text-align: left;">
                <span class="endpoint-text">{{ item.apiEndpoint }}</span>
              </td>
              <td class="text-center">
                <div class="action-group">
                  <v-btn variant="text" density="comfortable" class="action-btn edit-btn" icon @click="openEditDialog(item)">
                    <v-tooltip location="top">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="20" color="primary">mdi-pencil</v-icon>
                      </template>
                      <span>{{ $t('editTool') }}</span>
                    </v-tooltip>
                  </v-btn>
                  <div class="action-divider"></div>
                  <v-btn variant="text" density="comfortable" class="action-btn delete-btn" icon @click="deleteTool(item)">
                    <v-tooltip location="top">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="20" color="error">mdi-delete</v-icon>
                      </template>
                      <span>{{ $t('deleteTool') }}</span>
                    </v-tooltip>
                  </v-btn>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table-server>
      </v-card>
    </v-container>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="680" persistent scrollable>
      <v-card class="tool-dialog-new">
        <!-- Dialog Header -->
        <div class="tdn-header">
          <div class="tdn-header-left">
            <div class="tdn-header-icon">
              <v-icon size="22" color="white">mdi-plus</v-icon>
            </div>
            <div>
              <div class="tdn-title">{{ form.id ? $t('editTool') : $t('addNewTool') }}</div>
              <div class="tdn-subtitle">{{ $t('_configureApiEndpoint') }}</div>
            </div>
          </div>
          <v-btn icon variant="text" size="small" @click="closeDialog">
            <v-icon size="20" color="#6b7280">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-text class="tdn-body">
          <!-- Basic Information -->
          <div class="tdn-section-label">
            <v-icon size="16" color="#0FC4C2">mdi-information-outline</v-icon>
            <span>{{ $t('basicInformation') }}</span>
          </div>

          <v-text-field
            v-model="form.name"
            :placeholder="$t('toolName')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="tdn-field mb-3"
          >
            <template #prepend-inner>
              <v-icon size="16" color="#9ca3af" class="mr-1">mdi-tag-outline</v-icon>
            </template>
          </v-text-field>

          <v-textarea
            v-model="form.description"
            :placeholder="$t('description')"
            variant="outlined"
            density="compact"
            rows="2"
            hide-details="auto"
            class="tdn-field mb-4"
            auto-grow
          >
            <template #prepend-inner>
              <v-icon size="16" color="#9ca3af" class="mr-1 mt-1">mdi-text-box-outline</v-icon>
            </template>
          </v-textarea>

          <!-- API Configuration -->
          <div class="tdn-section-label">
            <span class="tdn-api-badge">API</span>
            <span>{{ $t('apiConfiguration') }}</span>
          </div>

          <div class="d-flex gap-3 mb-4">
            <v-text-field
              v-model="form.apiEndpoint"
              :placeholder="$t('apiEndpoint')"
              variant="outlined"
              density="compact"
              hide-details="auto"
              class="tdn-field"
              style="flex: 1;"
            >
              <template #prepend-inner>
                <v-icon size="16" color="#9ca3af" class="mr-1">mdi-link-variant</v-icon>
              </template>
            </v-text-field>
            <v-select
              v-model="form.httpMethod"
              :items="httpMethodItems"
              item-value="value"
              item-title="value"
              :label="$t('httpMethod')"
              variant="outlined"
              density="compact"
              hide-details="auto"
              class="tdn-field"
              style="max-width: 170px;"
            >
              <template #prepend-inner>
                <v-icon size="16" color="#9ca3af" class="mr-1">mdi-swap-horizontal</v-icon>
              </template>
              <template #selection="{ item }">
                <span :class="['method-badge', 'method-badge--' + item.raw.value]" style="font-size: 11px;">{{ item.raw.value }}</span>
              </template>
              <template #item="{ item, props }">
                <v-list-item v-bind="props" :title="undefined" min-height="52" style="padding: 10px 16px;">
                  <div class="d-flex align-center gap-3">
                    <span :class="['method-badge', 'method-badge--' + item.raw.value]" style="min-width: 56px;">{{ item.raw.value }}</span>
                    <span style="font-size: 14px; color: #374151; margin-left: 10px;">{{ item.raw.value }}</span>
                  </div>
                </v-list-item>
              </template>
            </v-select>
          </div>

          <!-- Headers -->
          <div class="tdn-section-row mb-2">
            <div class="tdn-section-label mb-0">
              <v-icon size="16" color="#374151">mdi-code-braces</v-icon>
              <span>{{ $t('headersLabel') }}</span>
            </div>
            <button type="button" class="tdn-add-btn" @click="addHeader">
              <v-icon size="14">mdi-plus</v-icon>
              {{ $t('addHeader') }}
            </button>
          </div>

          <div class="tdn-list-box mb-4">
            <div v-if="form.headers.length === 0" class="tdn-empty-state">
              <v-icon size="32" color="#d1d5db">mdi-code-braces</v-icon>
              <span>{{ $t('noHeaders') }}</span>
            </div>
            <div v-for="(header, idx) in form.headers" :key="idx" class="tdn-header-row">
              <v-text-field
                v-model="header.key"
                :placeholder="$t('Key')"
                variant="outlined"
                density="compact"
                hide-details
                class="tdn-field"
                style="flex: 1;"
              />
              <v-text-field
                v-model="header.value"
                :placeholder="$t('valueLabel')"
                variant="outlined"
                density="compact"
                hide-details
                class="tdn-field"
                style="flex: 1;"
              />
              <v-btn icon variant="text" size="x-small" @click="removeHeader(idx)">
                <v-icon size="16" color="#ef4444">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Parameters -->
          <div class="tdn-section-row mb-2">
            <div class="tdn-section-label mb-0">
              <v-icon size="16" color="#374151">mdi-code-parentheses</v-icon>
              <span>{{ $t('Parameters') }}</span>
              <span v-if="form.parameters.length > 0" class="tdn-count-badge">{{ form.parameters.length }}</span>
            </div>
            <button type="button" class="tdn-add-btn" @click="addParameter">
              <v-icon size="14">mdi-plus</v-icon>
              {{ $t('addParameter') }}
            </button>
          </div>

          <div class="tdn-list-box mb-4">
            <div v-if="form.parameters.length === 0" class="tdn-empty-state">
              <v-icon size="32" color="#d1d5db">mdi-code-parentheses</v-icon>
              <span>{{ $t('noParameters') }}</span>
              <span class="tdn-empty-hint">{{ $t('_clickAddParameterHint') }}</span>
            </div>
            <div v-for="(param, idx) in form.parameters" :key="idx" class="tdn-param-card">
              <!-- Param card header -->
              <div class="tdn-param-header">
                <div class="d-flex align-center gap-2">
                  <v-icon size="16" color="#0FC4C2">mdi-variable</v-icon>
                  <span class="tdn-param-title">{{ $t('parameter') }} {{ idx + 1 }}</span>
                </div>
                <div class="d-flex align-center gap-2">
                  <span v-if="param.required" class="tdn-required-chip">{{ $t('requiredParam') }}</span>
                  <span :class="['tdn-type-badge', 'tdn-type-badge--' + param.type]">{{ param.type }}</span>
                  <v-btn icon variant="text" size="x-small" @click="removeParameter(idx)">
                    <v-icon size="16" color="#ef4444">mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Name + Type + Required row -->
              <div class="d-flex gap-2 align-center mb-2">
                <v-text-field
                  v-model="param.name"
                  :placeholder="$t('parameterName')"
                  variant="outlined"
                  density="compact"
                  hide-details
                  :class="['tdn-field', 'tdn-param-field--' + param.type]"
                  style="flex: 1;"
                >
                  <template #prepend-inner>
                    <v-icon size="14" color="#9ca3af" class="mr-1">mdi-identifier</v-icon>
                  </template>
                </v-text-field>

                <v-select
                  v-model="param.type"
                  :items="paramTypeItems"
                  item-value="value"
                  item-title="label"
                  :label="$t('dataType')"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tdn-field"
                  style="max-width: 160px;"
                >
                  <template #prepend-inner>
                    <v-icon size="14" :color="paramTypeColor(param.type)" class="mr-1">{{ paramTypeIcon(param.type) }}</v-icon>
                  </template>
                  <template #item="{ item, props }">
                    <v-list-item v-bind="props" :title="undefined">
                      <template #prepend>
                        <v-icon size="18" :color="item.raw.color" class="mr-2">{{ item.raw.icon }}</v-icon>
                      </template>
                      <span style="font-size: 13px;">{{ item.raw.label }}</span>
                    </v-list-item>
                  </template>
                </v-select>

                <v-switch
                  v-model="param.required"
                  :label="$t('requiredParam')"
                  hide-details
                  density="compact"
                  color="#0FC4C2"
                  class="tdn-switch"
                />
              </div>

              <!-- Description -->
              <v-text-field
                v-model="param.description"
                :placeholder="$t('description')"
                variant="outlined"
                density="compact"
                hide-details
                class="tdn-field"
              >
                <template #prepend-inner>
                  <v-icon size="14" color="#9ca3af" class="mr-1">mdi-format-list-bulleted</v-icon>
                </template>
              </v-text-field>
            </div>
          </div>

          <!-- Test API -->
          <div class="tdn-section-row mb-2">
            <div class="tdn-section-label mb-0">
              <v-icon size="16" color="#0FC4C2">mdi-play-circle-outline</v-icon>
              <span>{{ $t('testApi') }}</span>
            </div>
            <button type="button" class="tdn-toggle-btn" @click="showTestPanel = !showTestPanel">
              <v-icon size="14">{{ showTestPanel ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              {{ showTestPanel ? $t('hideTestPanel') : $t('showTestPanel') }}
            </button>
          </div>

          <div v-if="showTestPanel" class="tdn-test-panel mb-2">
            <div class="tdn-section-label mb-3">
              <v-icon size="16" color="#374151">mdi-application-import</v-icon>
              <span>{{ $t('parameterValues') }}</span>
            </div>
            <div v-if="form.parameters.length === 0" class="tdn-empty-state" style="padding: 16px;">
              <span style="font-size: 13px; color: #9ca3af;">{{ $t('_noParametersDefined') }}</span>
            </div>
            <div v-else class="tdn-test-grid">
              <div v-for="param in form.parameters" :key="'test-' + param.name" class="tdn-test-cell">
                <v-text-field
                  v-model="testValues[param.name]"
                  :placeholder="param.name || $t('_enterText')"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tdn-field"
                >
                  <template #prepend-inner>
                    <span :class="['tdn-test-type-badge', 'tdn-type-' + param.type, 'mr-2']">{{ param.type }}</span>
                    <span v-if="param.required" class="tdn-required-star">*</span>
                  </template>
                </v-text-field>
                <div v-if="param.description" class="tdn-test-desc">{{ param.description }}</div>
              </div>
            </div>

            <div v-if="form.parameters.length > 0" class="d-flex justify-center mt-4">
              <button type="button" class="tdn-run-btn" :disabled="testRunning" @click="runTest">
                <v-icon size="16" class="mr-1">mdi-play</v-icon>
                {{ testRunning ? $t('running') : $t('runTest') }}
              </button>
            </div>

            <div v-if="testResult !== null" class="tdn-test-result mt-3">
              <div class="tdn-test-result-header">
                <span :class="testResultOk ? 'tdn-result-ok' : 'tdn-result-err'">
                  <v-icon size="14" class="mr-1">{{ testResultOk ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                  {{ testResultStatus }}
                </span>
                <span class="tdn-result-clear" @click="testResult = null">{{ $t('clear') }}</span>
              </div>
              <div v-if="testRequestUrl" class="tdn-test-result-url">
                <span class="tdn-test-result-url-label">{{ $t('_requestUrl') }}</span>
                <span class="tdn-test-result-url-value">{{ testRequestUrl }}</span>
              </div>
              <pre class="tdn-test-result-body">{{ testResult }}</pre>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="tdn-footer">
          <v-spacer />
          <v-btn variant="text" class="tdn-cancel-btn" @click="closeDialog">{{ $t('cancel') }}</v-btn>
          <v-btn
            variant="flat"
            color="#0FC4C2"
            :loading="saving"
            :disabled="!form.name"
            class="tdn-save-btn"
            @click="saveTool"
          >
            {{ form.id ? $t('saveTool') : $t('createTool') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

export default {
  name: 'ToolManagement',
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      showTestPanel: false,
      testValues: {},
      testRunning: false,
      testResult: null,
      testResultStatus: '',
      testResultOk: false,
      testRequestUrl: '',
      search: '',
      storageMessage: '',
      total: 0,
      tools: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
      },
      httpMethods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
      httpMethodItems: [
        { value: 'GET' },
        { value: 'POST' },
        { value: 'PUT' },
        { value: 'DELETE' },
        { value: 'PATCH' },
      ],
      paramTypes: ['string', 'number', 'boolean', 'array', 'object'],
      paramTypeItems: [
        { value: 'string',  label: 'string',  icon: 'mdi-format-text',         color: '#0FC4C2' },
        { value: 'number',  label: 'number',  icon: 'mdi-numeric',              color: '#f59e0b' },
        { value: 'boolean', label: 'boolean', icon: 'mdi-toggle-switch-outline', color: '#8b5cf6' },
        { value: 'array',   label: 'array',   icon: 'mdi-view-module-outline',  color: '#ec4899' },
        { value: 'object',  label: 'object',  icon: 'mdi-code-braces',          color: '#10b981' },
      ],
      form: this.createEmptyForm(),
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t('name'), key: 'name', sortable: true },
        { title: this.$t('description'), key: 'description', sortable: false },
        { title: this.$t('method'), key: 'httpMethod', sortable: true, width: 120 },
        { title: this.$t('endpoint'), key: 'apiEndpoint', sortable: false },
        { title: this.$t('manage'), key: 'actions', sortable: false, width: 110, align: 'center' },
      ];
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchTools();
      },
    },
  },
  mounted() {
    this.fetchTools();
  },
  methods: {
    createEmptyForm() {
      return {
        id: null,
        name: '',
        description: '',
        httpMethod: 'POST',
        apiEndpoint: '',
        headers: [],
        parameters: [],
      };
    },
    paramTypeIcon(type) {
      return this.paramTypeItems.find(i => i.value === type)?.icon ?? 'mdi-code-tags';
    },
    paramTypeColor(type) {
      return this.paramTypeItems.find(i => i.value === type)?.color ?? '#9ca3af';
    },
    addHeader() {
      this.form.headers.push({ key: '', value: '' });
    },
    removeHeader(idx) {
      this.form.headers.splice(idx, 1);
    },
    addParameter() {
      this.form.parameters.push({ name: '', type: 'string', required: false, description: '' });
    },
    removeParameter(idx) {
      this.form.parameters.splice(idx, 1);
    },
    reload() {
      this.options.page = 1;
      this.fetchTools();
    },
    fetchTools() {
      this.loading = true;
      const sort = this.options.sortBy?.[0] || {};
      const request = {
        page: this.options.page || 1,
        perPage: this.options.itemsPerPage || 10,
        search: this.search || null,
        sortBy: sort.key || null,
        sortDesc: sort.order === 'desc',
      };

      this.$call.GetTableAgentTool(
        request,
        (response) => {
          const payload = response?.data || response?.Data || {};
          this.storageMessage = response?.status === 0 || response?.Status === 0 ? '' : response?.message || response?.Message || '';
          this.total = payload.total ?? payload.Total ?? 0;
          this.tools = (payload.data || payload.Data || []).map(this.mapTool);
          this.loading = false;
        },
        (error) => {
          this.storageMessage = error?.response?.data || this.$t('_unableToLoadTools');
          this.tools = [];
          this.total = 0;
          this.loading = false;
        }
      );
    },
    mapTool(tool) {
      return {
        id: tool.id ?? tool.ID,
        name: tool.name ?? tool.Name ?? '',
        description: tool.description ?? tool.Description ?? '',
        httpMethod: tool.httpMethod ?? tool.HttpMethod ?? 'GET',
        apiEndpoint: tool.apiEndpoint ?? tool.ApiEndpoint ?? tool.url ?? tool.Url ?? '',
        headers: tool.headers ?? tool.Headers ?? [],
        parameters: tool.parameters ?? tool.Parameters ?? [],
      };
    },
    openCreateDialog() {
      this.form = this.createEmptyForm();
      this.showTestPanel = false;
      this.testValues = {};
      this.testResult = null;
      this.dialog = true;
    },
    openEditDialog(tool) {
      this.form = {
        id: tool.id,
        name: tool.name,
        description: tool.description,
        httpMethod: tool.httpMethod,
        apiEndpoint: tool.apiEndpoint,
        headers: (tool.headers || []).map(h => ({ key: h.Key ?? h.key ?? '', value: h.Value ?? h.value ?? '' })),
        parameters: (tool.parameters || []).map(p => ({
          name: p.Name ?? p.name ?? '',
          type: p.Type ?? p.type ?? 'string',
          required: p.Required ?? p.required ?? false,
          description: p.Description ?? p.description ?? '',
        })),
      };
      this.showTestPanel = false;
      this.testValues = {};
      this.testResult = null;
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.saving = false;
    },
    saveTool() {
      const request = {
        ID: this.form.id,
        Name: this.form.name,
        Description: this.form.description,
        ApiEndpoint: this.form.apiEndpoint,
        Url: this.form.apiEndpoint,
        HttpMethod: this.form.httpMethod,
        Headers: this.form.headers.map(h => ({ Key: h.key, Value: h.value })),
        Parameters: this.form.parameters.map(p => ({
          Name: p.name,
          Type: p.type,
          Required: p.required,
          Description: p.description,
        })),
      };

      this.saving = true;
      const onSuccess = (response) => {
        this.saving = false;
        if (response?.status === 0 || response?.Status === 0) {
          this.dialog = false;
          this.fetchTools();
          Swal.fire({ icon: 'success', title: this.$t('saved'), showConfirmButton: false, timer: 1200 });
          return;
        }
        Swal.fire({ icon: 'error', title: this.$t('saveFailed'), text: response?.message || response?.Message || this.$t('_unableToSaveTool') });
      };
      const onError = () => {
        this.saving = false;
      };

      if (this.form.id) {
        this.$call.UpdateAgentTool(request, onSuccess, onError);
        return;
      }

      this.$call.CreateAgentTool(request, onSuccess, onError);
    },
    buildTestRequestUrl(endpoint, method, parameters) {
      let url = endpoint;
      Object.keys(parameters).forEach((name) => {
        const value = parameters[name] ?? '';
        url = url.split(`{{${name}}}`).join(encodeURIComponent(value));
      });

      if (method === 'GET' && Object.keys(parameters).length > 0 && !url.includes('?')) {
        url = `${url}?${new URLSearchParams(parameters).toString()}`;
      }

      return url;
    },
    maskUrlSecrets(url) {
      return url.replace(/([?&](key|apikey|api_key|token|access_token|secret)=)[^&]*/gi, '$1***');
    },
    formatTestResponseBody(body) {
      if (!body) return '';
      try {
        return JSON.stringify(JSON.parse(body), null, 2);
      } catch {
        return body;
      }
    },
    runTest() {
      const endpoint = this.form.apiEndpoint;
      if (!endpoint) {
        Swal.fire({ icon: 'warning', title: this.$t('noEndpoint'), text: this.$t('_fillApiEndpointBeforeTest') });
        return;
      }
      this.testRunning = true;
      this.testResult = null;
      this.testRequestUrl = '';

      const method = (this.form.httpMethod || 'GET').toUpperCase();
      const parameterValues = {};
      this.form.parameters.forEach(p => {
        if (p.name) parameterValues[p.name] = this.testValues[p.name] ?? '';
      });

      // Show the expected URL right away; the server replies with the URL it actually called.
      this.testRequestUrl = this.maskUrlSecrets(this.buildTestRequestUrl(endpoint, method, parameterValues));

      // The call must run server-side: the browser cannot reach third-party endpoints (CSP connect-src
      // + CORS), and the flow engine calls tools from the server anyway, so this matches the real run.
      const request = {
        apiEndpoint: endpoint,
        httpMethod: method,
        headers: this.form.headers.filter(h => h.key).map(h => ({ key: h.key, value: h.value })),
        parameterValues,
      };

      this.$call.TestAgentTool(
        request,
        (response) => {
          this.testRunning = false;
          const isOk = response?.status === 0 || response?.Status === 0;
          const payload = response?.data || response?.Data;

          if (!isOk || !payload) {
            this.testResultOk = false;
            this.testResultStatus = this.$t('error');
            this.testResult = response?.message || response?.Message || this.$t('_requestFailed');
            return;
          }

          const requestUrl = payload.requestUrl ?? payload.RequestUrl;
          if (requestUrl) {
            this.testRequestUrl = this.maskUrlSecrets(requestUrl);
          }

          const errorMessage = payload.errorMessage ?? payload.ErrorMessage;
          if (errorMessage) {
            this.testResultOk = false;
            this.testResultStatus = this.$t('error');
            this.testResult = errorMessage;
            return;
          }

          const statusCode = payload.statusCode ?? payload.StatusCode ?? 0;
          const statusText = payload.statusText ?? payload.StatusText ?? '';
          this.testResultOk = payload.success ?? payload.Success ?? false;
          this.testResultStatus = `${statusCode} ${statusText}`.trim();
          this.testResult = this.formatTestResponseBody(payload.responseBody ?? payload.ResponseBody);
        },
        (error) => {
          this.testRunning = false;
          this.testResultOk = false;
          this.testResultStatus = this.$t('error');
          this.testResult = error?.response?.data || error?.message || this.$t('_requestFailed');
        }
      );
    },
    deleteTool(tool) {
      Swal.fire({
        icon: 'warning',
        title: this.$t('_deleteToolConfirm'),
        text: tool.name,
        showCancelButton: true,
        confirmButtonText: this.$t('delete'),
        cancelButtonText: this.$t('cancel'),
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.$call.DeleteAgentTool(
          tool.id,
          (response) => {
            if (response?.status === 0 || response?.Status === 0) {
              this.fetchTools();
              Swal.fire({ icon: 'success', title: this.$t('deleted'), showConfirmButton: false, timer: 1200 });
              return;
            }
            Swal.fire({ icon: 'error', title: this.$t('deleteFailed'), text: response?.message || response?.Message || this.$t('_unableToDeleteTool') });
          },
          () => {}
        );
      });
    },
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 16px !important;
  margin-bottom: 2rem;
  margin-top: 1rem;
}

.search-input {
  max-width: 280px;
  background: white;
  border: 1px solid #C9C9C9;
  border-radius: 12px;
}

.search-input :deep(.v-field__field) {
  align-items: center;
}

.search-input :deep(input) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  line-height: 20px;
  min-height: 40px;
  display: flex;
  align-items: center;
  font-size: 13px;
}

.search-icon {
  margin-left: 12px;
  margin-top: -2px;
}

.refresh-btn {
  text-transform: none !important;
  border-radius: 12px !important;
}

.tool-add {
  display: flex;
  flex-direction: row;
  background: linear-gradient(100deg, #C32E0E 0%, #F67760 100%) !important;
  border-radius: 12px;
  min-width: 128px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 12px;
  margin-right: 16px;
  cursor: pointer;
  border: none;
  transition: background 0.2s ease;
}

.tool-add:hover {
  background: linear-gradient(100deg, #F36243 0%, #FF8E7A 100%) !important;
}

.action-group {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.action-btn {
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
}

.action-btn:hover {
  transform: scale(1.1) !important;
}

.action-divider {
  width: 1px;
  height: 20px;
  background: #e5e7eb;
  margin: 0 4px;
}

.endpoint-text {
  display: inline-block;
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.no-data-container {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.no-data-subtitle {
  font-size: 14px;
  color: #9ca3af;
  margin: 4px 0 0;
}

/* HTTP Method badge */
.method-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  padding: 3px 10px;
  color: white;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.method-badge--GET    { background: #3b82f6; }
.method-badge--POST   { background: #22c55e; }
.method-badge--PUT    { background: #f59e0b; }
.method-badge--DELETE { background: #ef4444; }
.method-badge--PATCH  { background: #8b5cf6; }

/* New dialog styles */
.tool-dialog-new {
  border-radius: 16px !important;
  overflow: hidden;
}

.tdn-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
}

.tdn-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tdn-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #0FC4C2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tdn-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.tdn-subtitle {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.tdn-body {
  padding: 20px !important;
  overflow-y: auto;
  max-height: 65vh;
}

.tdn-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}

.tdn-api-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: #0FC4C2;
  border: 1.5px solid #0FC4C2;
  border-radius: 4px;
  padding: 0 4px;
  line-height: 14px;
  height: 16px;
  letter-spacing: 0.5px;
}

.tdn-section-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tdn-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #0a8381;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}

.tdn-add-btn:hover {
  background: #0aafad;
}

.tdn-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  color: #0FC4C2;
  border: 1.5px solid #0FC4C2;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.tdn-toggle-btn:hover {
  background: rgba(15, 196, 194, 0.08);
}

.tdn-list-box {
  border: 1.5px dashed #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #fafafa;
  min-height: 64px;
}

.tdn-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 20px;
  color: #9ca3af;
  font-size: 13px;
}

.tdn-empty-hint {
  font-size: 11px;
  color: #d1d5db;
  text-align: center;
}

.tdn-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.tdn-header-row:last-child {
  margin-bottom: 0;
}

.tdn-param-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 8px;
}

.tdn-param-card:last-child {
  margin-bottom: 0;
}

.tdn-param-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.tdn-param-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.tdn-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e5e7eb;
  color: #616773;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0 7px;
  height: 18px;
  min-width: 18px;
}

/* Type badge in param card header */
.tdn-type-badge {
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
  padding: 2px 10px;
}
.tdn-type-badge--string  { background: #e0fafa; color: #097c7b; }
.tdn-type-badge--number  { background: #fef3c7; color: #986207; }
.tdn-type-badge--boolean { background: #ede9fe; color: #774fd3; }
.tdn-type-badge--array   { background: #fce7f3; color: #b83877; }
.tdn-type-badge--object  { background: #d1fae5; color: #0b7c56; }

/* Required chip in param card header */
.tdn-required-chip {
  background: #fce7f3;
  color: #b83877;
  font-size: 10px;
  font-weight: 700;
  border-radius: 20px;
  padding: 2px 8px;
  letter-spacing: 0.3px;
}

/* Color-coded border on param name input */
.tdn-param-field--string  :deep(.v-field) { border-color: #0FC4C2 !important; }
.tdn-param-field--number  :deep(.v-field) { border-color: #f59e0b !important; }
.tdn-param-field--boolean :deep(.v-field) { border-color: #8b5cf6 !important; }
.tdn-param-field--array   :deep(.v-field) { border-color: #ec4899 !important; }
.tdn-param-field--object  :deep(.v-field) { border-color: #10b981 !important; }

/* Required switch label */
.tdn-switch :deep(.v-label) {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  opacity: 1;
}

.tdn-test-panel {
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  background: #f9fafb;
}



.tdn-field :deep(.v-field) {
  border-radius: 10px;
}

.tdn-switch {
  flex-shrink: 0;
}

/* Test panel grid */
.tdn-test-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.tdn-test-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tdn-test-desc {
  font-size: 11px;
  color: #9ca3af;
  padding-left: 4px;
}

.tdn-required-star {
  color: #f59e0b;
  font-weight: 700;
  font-size: 14px;
  margin-right: 4px;
}

/* Type badge colors for test panel */
.tdn-test-type-badge {
  font-size: 10px;
  font-weight: 700;
  border-radius: 20px;
  padding: 2px 8px;
  white-space: nowrap;
}

.tdn-type-string { background: #0a8381; color: white; }
.tdn-type-number { background: #a26907; color: white; }
.tdn-type-boolean { background: #6164ed; color: white; }
.tdn-type-array { background: #cb3e84; color: white; }
.tdn-type-object { background: #8458ea; color: white; }

.tdn-run-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #0a8381;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 32px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
  transition: background 0.2s;
  min-width: 160px;
}

.tdn-run-btn:hover:not(:disabled) {
  background: #0aafad;
}

.tdn-run-btn:disabled {
  background: #a0e4e3;
  cursor: not-allowed;
}

.tdn-test-result {
  background: #1e293b;
  border-radius: 10px;
  overflow: hidden;
}

.tdn-test-result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #0f172a;
  font-size: 12px;
  font-weight: 600;
}

.tdn-result-ok {
  display: flex;
  align-items: center;
  color: #34d399;
}

.tdn-result-err {
  display: flex;
  align-items: center;
  color: #f87171;
}

.tdn-result-clear {
  color: #64748b;
  font-size: 11px;
  cursor: pointer;
  text-decoration: underline;
}

.tdn-result-clear:hover {
  color: #94a3b8;
}

.tdn-test-result-url {
  display: flex;
  gap: 6px;
  padding: 6px 12px;
  background: #172033;
  font-size: 10px;
  line-height: 1.5;
  font-family: 'Consolas', 'Monaco', monospace;
  word-break: break-all;
}

.tdn-test-result-url-label {
  color: #64748b;
  flex-shrink: 0;
}

.tdn-test-result-url-value {
  color: #94a3b8;
}

.tdn-test-result-body {
  padding: 12px;
  margin: 0;
  font-size: 11px;
  color: #e2e8f0;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
  font-family: 'Consolas', 'Monaco', monospace;
}

.tdn-footer {
  padding: 12px 20px 16px !important;
}

.tdn-cancel-btn {
  text-transform: none !important;
  color: #6b7280 !important;
  font-weight: 500;
}

.tdn-save-btn {
  text-transform: none !important;
  color: white !important;
  font-weight: 600;
  border-radius: 10px !important;
  min-width: 120px;
}

:deep(.v-data-table-server) {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(thead) {
  background-color: #FFE4E1 !important;
}

:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  color: #374151 !important;
}

:deep(.v-data-table tbody tr) {
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.v-data-table tbody tr:hover) {
  background: rgba(0, 0, 0, 0.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
</style>
